import type { Status, TrackingLinkAccessType, TrackingLinkStatus } from "../types";

const STATUS_BY_ID: Record<string, TrackingLinkStatus> = {
  R5tW0uX4yY9cF6dH2aI8: "active",
  W0yB5zC9dD4hK1iM7fN3: "expired",
  S6uX1vY5zZ0dG7eI3bJ9: "inactive",
};
const KNOWN: readonly string[] = ["active", "inactive", "expired", "pending"];

// El estado llega como objeto del catálogo ({ id, name, color }) y su name viene
// a veces con mayúscula («Pending»). La vista de lista lo comparaba como string
// (=== "pending") y nunca acertaba: siempre «Tiempo restante», nunca el botón
// Configurar de los pendientes ni Activar de los inactivos. Una sola lectura
// para lista, tarjeta y contador.
export const trackingStatusKey = (status: Status | null | undefined): TrackingLinkStatus | null => {
  if (!status) return null;
  if (STATUS_BY_ID[status.id]) return STATUS_BY_ID[status.id];
  const name = status.name?.toLowerCase() ?? "";
  return KNOWN.includes(name) ? (name as TrackingLinkStatus) : null;
};

export const getStatusColor = (status: Status | null): string => {
  if (!status) return "bg-[#FFFBEB] text-[#B45309]";
  
  // Usar el color del status si está definido, sino usar colores por defecto
  if (status.color) return status.color;
  
  // Mapeo por ID para mayor precisión
  switch (status.id) {
    case "R5tW0uX4yY9cF6dH2aI8": // Active
      return "bg-primary-500 text-primary-800";
    case "W0yB5zC9dD4hK1iM7fN3": // Expired
      return "bg-[#FEF2F2] text-[#DC2626]";
    case "S6uX1vY5zZ0dG7eI3bJ9": // Inactive
      return "bg-[#F2F4F5] text-[#6B7280]";
    default:
      // Fallback por nombre
      switch (status.name) {
        case "active":
        case "Active":
          return "bg-primary-500 text-primary-800";
        case "expired":
        case "Expired":
          return "bg-[#FEF2F2] text-[#DC2626]";
        case "pending":
        case "Pending":
          return "bg-[#F2F4F5] text-[#6B7280]";
        case "inactive":
        case "Inactive":
          return "bg-[#F2F4F5] text-[#6B7280]";
        default:
          return "bg-[#FFFBEB] text-[#B45309]";
      }
  }
};

export const getStatusText = (status: Status | null): string => {
  if (!status) return "Desconocido";
  return status.name;
};

export const getAccessTypeColor = (
  accessType: TrackingLinkAccessType,
): string => {
  switch (accessType) {
    case "public":
      return "bg-[#F2F4F5] text-[#4B5563";
    case "protected":
      return "bg-[#F2F4F5] text-[#4B5563";
    default:
      return "bg-gray-100 text-gray-600";
  }
};

export const getAccessTypeText = (
  accessType: TrackingLinkAccessType,
): string => {
  switch (accessType) {
    case "public":
      return "Público";
    case "protected":
      return "Con código";
    default:
      return accessType;
  }
};

export const getAccessTypeIcon = (
  accessType: TrackingLinkAccessType,
): string => {
  switch (accessType) {
    case "public":
      return "ri-global-line text-[#4B5563]";
    case "protected":
      return "ri-lock-line text-[#4B5563]";
    default:
      return "ri-link";
  }
};

export const isExpiringSoon = (expirationDate: any): boolean => {
  if (!expirationDate) return false;

  const expDate = expirationDate._seconds
    ? new Date(expirationDate._seconds * 1000)
    : new Date(expirationDate);

  const now = new Date();
  const hoursUntilExpiration =
    (expDate.getTime() - now.getTime()) / (1000 * 60 * 60);

  return hoursUntilExpiration > 0 && hoursUntilExpiration <= 24;
};

export const getExpirationText = (
  expirationDate: any,
  status: TrackingLinkStatus | null,
): { text: string; isWarning: boolean } => {
  if (!expirationDate) {
    return { text: "No definida", isWarning: false };
  }

  const expDate = expirationDate._seconds
    ? new Date(expirationDate._seconds * 1000)
    : new Date(expirationDate);

  const now = new Date();
  const diffMs = expDate.getTime() - now.getTime();

  if (status === "expired" || diffMs < 0) {
    const absDiffMs = Math.abs(diffMs);
    const days = Math.floor(absDiffMs / (1000 * 60 * 60 * 24));
    if (days > 0) {
      return { text: `${days} día${days > 1 ? "s" : ""}`, isWarning: false };
    }
    const hours = Math.floor(absDiffMs / (1000 * 60 * 60));
    return { text: `${hours} hora${hours > 1 ? "s" : ""}`, isWarning: false };
  }

  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

  if (days > 0) {
    return {
      text: `${days} día${days > 1 ? "s" : ""} ${hours}h`,
      isWarning: days === 0 && hours <= 4,
    };
  }

  return {
    text: `${hours} hora${hours > 1 ? "s" : ""}`,
    isWarning: hours <= 4,
  };
};

export const getRemainingTime = (
  expirationDate: any,
  status: TrackingLinkStatus | null,
): { text: string; isWarning: boolean } => {
  if (!expirationDate) {
    return { text: "--:--:--", isWarning: false };
  }

  const expDate = expirationDate._seconds
    ? new Date(expirationDate._seconds * 1000)
    : new Date(expirationDate);

  const now = new Date();
  const diffMs = expDate.getTime() - now.getTime();

  if (status === "expired" || diffMs < 0) {
    return { text: "Expirado", isWarning: false };
  }

  const totalSeconds = Math.floor(diffMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const formattedTime = `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;

  const isWarning = totalSeconds <= 24 * 60 * 60; // Less than 24 hours

  return { text: formattedTime, isWarning };
};

export const getRemainingTimeWithDate = (
  expirationDate: any,
  status: TrackingLinkStatus | null,
  currentTime: Date = new Date(),
): { text: string; isWarning: boolean } => {
  if (!expirationDate) {
    return { text: "--:--:--", isWarning: false };
  }

  const expDate = expirationDate._seconds
    ? new Date(expirationDate._seconds * 1000)
    : new Date(expirationDate);

  const diffMs = expDate.getTime() - currentTime.getTime();

  if (status === "expired" || diffMs < 0) {
    return { text: "Expirado", isWarning: false };
  }

  const totalSeconds = Math.floor(diffMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const formattedTime = `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;

  const isWarning = totalSeconds <= 24 * 60 * 60; // Less than 24 hours

  return { text: formattedTime, isWarning };
};
